/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { ReactNode } from 'react';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { NoAccess } from '@/components/NoAccess';
import { PageLoading } from '@/components/loading/PageLoading';
import { useHasCapability } from '@/lib/userRole';
import { useRoleView } from '@/stores/roleView';
import type { Capability } from '@/lib/roles';

/** Route guard that renders children only when the user holds {@link capability}. */
export function RequireCapability({
  capability,
  children,
}: {
  capability: Capability;
  children: ReactNode;
}) {
  const { account } = useAuth();
  const override = useRoleView((s) => s.override);
  const allowed = useHasCapability(capability);

  // Don't flash NoAccess while the real account (and thus its roles) is still resolving.
  if (!override && !account) return <PageLoading />;
  if (!allowed) return <NoAccess />;
  return <>{children}</>;
}
